import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { computeVisibleChipCount } from './utils';

const DEFAULT_VISIBLE_CHIP_COUNT = 2;
const FALLBACK_CHIP_FONT = '400 12px Roboto, sans-serif';
const FALLBACK_CHIP_OVERHEAD_PX = 56;
const FALLBACK_CHAR_WIDTH_PX = 7;
const CHIP_GAP_PX = 4;
const END_ADORNMENT_RESERVE_PX = 24;

const measureTextWidth = (
  text: string,
  font: string,
  canvas: HTMLCanvasElement,
): number => {
  const context = canvas.getContext('2d');
  if (!context) {
    return text.length * FALLBACK_CHAR_WIDTH_PX;
  }
  context.font = font;
  return context.measureText(text).width;
};

// HACK: This hook would probably no longer be needed once we've updated fmlx-common-ui
export const useVisibleChipCount = (
  containerRef: RefObject<HTMLElement>,
  labels: string[],
): number => {
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(DEFAULT_VISIBLE_CHIP_COUNT, labels.length),
  );
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useLayoutEffect(() => {
    if (labels.length === 0) {
      setVisibleCount(0);
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      setVisibleCount(Math.min(DEFAULT_VISIBLE_CHIP_COUNT, labels.length));
      return undefined;
    }

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;

    const recompute = () => {
      const inputRoot = container.querySelector(
        '.MuiInputBase-root',
      ) as HTMLElement | null;
      const sampleChip = container.querySelector('.fmlx-chip') as HTMLElement | null;
      const sampleLabel = sampleChip?.querySelector(
        '.MuiChip-label',
      ) as HTMLElement | null;
      const endAdornment = container.querySelector(
        '.MuiAutocomplete-endAdornment',
      ) as HTMLElement | null;

      const font = sampleLabel ? getComputedStyle(sampleLabel).font : '';
      const resolvedFont = font || FALLBACK_CHIP_FONT;

      const overhead =
        sampleChip && sampleLabel
          ? sampleChip.getBoundingClientRect().width -
            measureTextWidth(sampleLabel.textContent ?? '', resolvedFont, canvas)
          : FALLBACK_CHIP_OVERHEAD_PX;

      const reservedForAdornment = endAdornment
        ? endAdornment.getBoundingClientRect().width
        : END_ADORNMENT_RESERVE_PX;

      const availableWidth = (inputRoot?.clientWidth ?? 0) - reservedForAdornment;

      const moreIndicatorWidth =
        measureTextWidth(`+${labels.length}`, resolvedFont, canvas) + 8;

      setVisibleCount(
        computeVisibleChipCount(
          labels,
          (label) => measureTextWidth(label, resolvedFont, canvas),
          Math.max(overhead, 0),
          CHIP_GAP_PX,
          Math.max(availableWidth, 0),
          moreIndicatorWidth,
        ),
      );
    };

    recompute();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, labels]);

  return visibleCount;
};
